import type { FC } from 'react'
import { useMemo } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, useTheme } from '@mui/material'
import { Highlight, themes } from 'prism-react-renderer'
import { diffLines } from 'diff'
import type { FileUpdate } from '@/infra/sse/conversation/payloads'
import { Prism } from '../utils/prism-languages'

type ToolDiffBlockProps = PreToolDiffProps | PostToolDiffProps

interface PreToolDiffProps {
  readonly mode: 'pre-tool'
  readonly oldString: string
  readonly newString: string
  readonly language: string
}

interface PostToolDiffProps {
  readonly mode: 'post-tool'
  readonly updates: readonly FileUpdate[]
  readonly language: string
}

interface ParsedDiffLine {
  type: 'addition' | 'removal' | 'context'
  content: string
  oldLineNumber: number | null
  newLineNumber: number | null
}

export const ToolDiffBlock: FC<ToolDiffBlockProps> = (props) => {
  const theme = useTheme()

  if (props.mode === 'pre-tool') {
    return (
      <PreToolDiff
        oldString={props.oldString}
        newString={props.newString}
        language={props.language}
        theme={theme}
      />
    )
  }

  return <PostToolDiff updates={props.updates} language={props.language} theme={theme} />
}

const PreToolDiff: FC<Omit<PreToolDiffProps, 'mode'> & { theme: Theme }> = ({
  oldString,
  newString,
  language,
  theme,
}) => {
  const diffLines = useMemo(() => generateSimpleDiff(oldString, newString), [oldString, newString])

  return (
    <Box sx={diffContainerSx}>
      {diffLines.map((line, idx) => (
        <DiffLine
          key={`diff-line-${idx}-${line.content.slice(0, 20)}`}
          line={line}
          language={language}
          theme={theme}
          showLineNumbers={false}
        />
      ))}
    </Box>
  )
}

const PostToolDiff: FC<Omit<PostToolDiffProps, 'mode'> & { theme: Theme }> = ({
  updates,
  language,
  theme,
}) => {
  const parsedHunks = useMemo(() => updates.map((hunk) => parseDiffLines(hunk)), [updates])

  return (
    <Box sx={diffContainerSx}>
      {parsedHunks.map((hunk, hunkIdx) =>
        hunk.map((line, lineIdx) => (
          <DiffLine
            key={`hunk-${hunkIdx}-line-${lineIdx}-${line.content.slice(0, 20)}`}
            line={line}
            language={language}
            theme={theme}
            showLineNumbers={true}
          />
        )),
      )}
    </Box>
  )
}

interface DiffLineProps {
  readonly line: ParsedDiffLine
  readonly language: string
  readonly theme: Theme
  readonly showLineNumbers: boolean
}

const DiffLine: FC<DiffLineProps> = ({ line, language, theme, showLineNumbers }) => {
  const codeWithoutPrefix =
    line.content.startsWith('+') || line.content.startsWith('-')
      ? line.content.slice(1)
      : line.content

  const getDiffPrefix = (type: ParsedDiffLine['type']): string => {
    if (type === 'addition') return '+'
    if (type === 'removal') return '-'
    return ' '
  }

  return (
    <Highlight
      theme={theme.palette.mode === 'dark' ? themes.vsDark : themes.vsLight}
      code={codeWithoutPrefix}
      language={language}
      prism={Prism}
    >
      {({ tokens, getLineProps, getTokenProps }) => {
        const firstLine = tokens[0]
        if (firstLine === undefined) {
          return null
        }

        let oldLineDisplay = '    '
        if (line.oldLineNumber !== null) {
          oldLineDisplay = String(line.oldLineNumber).padStart(4, ' ')
        }

        let newLineDisplay = '    '
        if (line.newLineNumber !== null) {
          newLineDisplay = String(line.newLineNumber).padStart(4, ' ')
        }

        return (
          <Box {...getLineProps({ line: firstLine })} sx={diffLineSx(theme, line.type)}>
            {showLineNumbers && (
              <>
                <Box component="span" sx={lineNumberSx(theme)}>
                  {oldLineDisplay}
                </Box>

                <Box component="span" sx={lineNumberSx(theme)}>
                  {newLineDisplay}
                </Box>
              </>
            )}

            <Box component="span" sx={diffPrefixSx(theme, line.type)}>
              {getDiffPrefix(line.type)}
            </Box>

            <Box component="span" sx={codeContentSx}>
              {firstLine.map((token, tokenIdx) => (
                <Box
                  component="span"
                  key={`token-${tokenIdx}-${token.content.slice(0, 10)}`}
                  {...getTokenProps({ token })}
                />
              ))}
            </Box>
          </Box>
        )
      }}
    </Highlight>
  )
}

const diffContainerSx: SxProps<Theme> = {
  fontFamily: '"Fira Code", "JetBrains Mono", "SF Mono", Monaco, monospace',
  fontSize: 'inherit',
  lineHeight: 1.5,
  overflow: 'auto',
}

const diffLineSx = (theme: Theme, type: ParsedDiffLine['type']): SxProps<Theme> => {
  const isDark = theme.palette.mode === 'dark'
  let backgroundColor = 'transparent'

  if (type === 'addition') {
    backgroundColor = isDark ? 'rgba(46, 160, 67, 0.15)' : 'rgba(46, 160, 67, 0.12)'
  } else if (type === 'removal') {
    backgroundColor = isDark ? 'rgba(248, 81, 73, 0.15)' : 'rgba(248, 81, 73, 0.12)'
  }

  return {
    display: 'flex',
    alignItems: 'center',
    backgroundColor,
  }
}

const lineNumberSx = (theme: Theme): SxProps<Theme> => ({
  display: 'inline-block',
  width: 48,
  px: 1,
  textAlign: 'right',
  color: theme.palette.text.disabled,
  userSelect: 'none',
  flexShrink: 0,
  fontSize: '0.75em',
})

const diffPrefixSx = (theme: Theme, type: ParsedDiffLine['type']): SxProps<Theme> => {
  let color = theme.palette.text.disabled
  if (type === 'addition') {
    color = theme.palette.success.main
  } else if (type === 'removal') {
    color = theme.palette.error.main
  }

  const fontWeight = type === 'context' ? 'normal' : 'bold'

  return {
    display: 'inline-block',
    width: 16,
    textAlign: 'center',
    color,
    fontWeight,
    userSelect: 'none',
    mr: 0.5,
  }
}

const codeContentSx: SxProps<Theme> = {
  flex: 1,
  px: 1,
  whiteSpace: 'pre-wrap',
  overflowWrap: 'break-word',
  overflowX: 'hidden',
}

function parseDiffLines(hunk: FileUpdate): ParsedDiffLine[] {
  let oldLine = hunk.oldStart
  let newLine = hunk.newStart

  return hunk.lines.map((line) => {
    let lineType: ParsedDiffLine['type'] = 'context'
    if (line.startsWith('+')) {
      lineType = 'addition'
    } else if (line.startsWith('-')) {
      lineType = 'removal'
    }

    const result: ParsedDiffLine = {
      type: lineType,
      content: line,
      oldLineNumber: null,
      newLineNumber: null,
    }

    if (line.startsWith('+')) {
      result.newLineNumber = newLine
      newLine++
    } else if (line.startsWith('-')) {
      result.oldLineNumber = oldLine
      oldLine++
    } else {
      result.oldLineNumber = oldLine
      result.newLineNumber = newLine
      oldLine++
      newLine++
    }

    return result
  })
}

function generateSimpleDiff(oldString: string, newString: string): ParsedDiffLine[] {
  const changes = diffLines(oldString, newString)
  const result: ParsedDiffLine[] = []
  let oldLineNumber = 1
  let newLineNumber = 1

  for (const change of changes) {
    const lines = change.value.split('\n')
    if (lines.at(-1) === '') {
      lines.pop()
    }

    for (const line of lines) {
      if (change.added) {
        result.push({
          type: 'addition',
          content: `+${line}`,
          oldLineNumber: null,
          newLineNumber: newLineNumber++,
        })
      } else if (change.removed) {
        result.push({
          type: 'removal',
          content: `-${line}`,
          oldLineNumber: oldLineNumber++,
          newLineNumber: null,
        })
      } else {
        result.push({
          type: 'context',
          content: ` ${line}`,
          oldLineNumber: oldLineNumber++,
          newLineNumber: newLineNumber++,
        })
      }
    }
  }

  return result
}
